import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { PDFDocument, RGB, rgb } from 'pdf-lib';
import MigrationSuggestionDTO from '@src/adapters/dto/migration/MigrationSuggestionDTO';
import { MigrationItemSuggestionStatus } from '@src/constants/MigrationItemSuggestionStatus';
import { Box } from '@mui/material';
import { useMenu } from '@src/providers/MenuProvider';

interface Props {
  pdfUrl: string | undefined;
  suggestions: MigrationSuggestionDTO[];
  highlightItemId: string;
  rotation: number;
  pageNumber: number;
  zoomLevel: number;
  setNumberOfPages(pages: number): void;
}

type DocumentLoadSuccessParams = {
  numPages: number;
};

const StyledDocument = styled(Document)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
}));

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfHighlighter = ({
  pdfUrl,
  suggestions,
  highlightItemId,
  rotation,
  pageNumber,
  zoomLevel,
  setNumberOfPages,
}: Props) => {
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [currentRotation, setCurrentRotation] = useState(0);
  const [pdfData, setPdfData] = useState('');
  const { menuWidth } = useMenu();
  const containerRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);
  const startCoords = useRef({ x: 0, y: 0 });
  const scrollCoords = useRef({ left: 0, top: 0 });

  useEffect(() => {
    setCurrentRotation(rotation || 0);
  }, [rotation]);

  useEffect(() => {
    if (pageNumber) {
      setCurrentPageNumber(pageNumber);
    }
  }, [pageNumber]);

  const onDocumentLoadSuccess = ({ numPages }: DocumentLoadSuccessParams) => {
    if (setNumberOfPages) {
      setNumberOfPages(numPages);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (containerRef.current) {
      isDragging.current = true;
      startCoords.current = { x: e.clientX, y: e.clientY };
      scrollCoords.current = {
        left: containerRef.current.scrollLeft,
        top: containerRef.current.scrollTop,
      };
      containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current && containerRef.current) {
      const deltaX = e.clientX - startCoords.current.x;
      const deltaY = e.clientY - startCoords.current.y;

      containerRef.current.scrollLeft = scrollCoords.current.left - deltaX;
      containerRef.current.scrollTop = scrollCoords.current.top - deltaY;
    }
  };

  const handleMouseUp = () => {
    if (isDragging.current && containerRef.current) {
      isDragging.current = false;
      containerRef.current.style.cursor = 'grab';
    }
  };

  useEffect(() => {
    const loadAndUpdateFile = async () => {
      if (pdfUrl) {
        const response = await fetch(pdfUrl);
        const existingPdfBytes = await response.arrayBuffer();
        if (existingPdfBytes) {
          const pdfDoc = await PDFDocument.load(existingPdfBytes);
          if (pdfDoc) {
            const pages = pdfDoc.getPages();
            const firstPage = pages[0];
            suggestions.forEach((s) => {
              if (s.boundingPolygons) {
                let boxColor: RGB | undefined;
                let highlightColor: RGB | undefined;
                let highlightOpacity = 0;

                if (highlightItemId && s.id === highlightItemId) {
                  //Selected Item
                  highlightColor = rgb(0.651, 0.914, 1); //Light Blue
                  highlightOpacity = 0.25;
                  boxColor = rgb(0.129, 0.588, 0.953); //Dark Blue
                } else {
                  switch (s.status) {
                    case MigrationItemSuggestionStatus.Ignored:
                      highlightColor = rgb(0, 0, 0); //Light Grey with opacity
                      highlightOpacity = 0.2;
                      break;
                    case MigrationItemSuggestionStatus.Matched:
                      highlightColor = rgb(0, 0.722, 0.251); //Green
                      highlightOpacity = 0.25;
                      break;
                    case MigrationItemSuggestionStatus.Unchecked:
                      highlightColor = rgb(0.867, 0.522, 0.01); //yellow
                      highlightOpacity = 0.2;
                      break;
                    case MigrationItemSuggestionStatus.Checked:
                      highlightColor = rgb(0.063, 0, 1);
                      highlightOpacity = 0.2;
                      break;
                  }
                }

                if (highlightColor) {
                  const { height } = firstPage.getSize();
                  const x = Math.min(
                    ...s.boundingPolygons.map((p) => p.x * 72)
                  );
                  const y = Math.min(
                    ...s.boundingPolygons.map((p) => height - p.y * 72)
                  );
                  const width =
                    Math.max(...s.boundingPolygons.map((p) => p.x * 72)) - x;
                  const calculatedHeight =
                    Math.max(
                      ...s.boundingPolygons.map((p) => height - p.y * 72)
                    ) - y;

                  firstPage.drawRectangle({
                    x,
                    y,
                    width,
                    height: calculatedHeight,
                    color: highlightColor,
                    borderColor: highlightColor,
                    borderWidth: 0,
                    opacity: highlightOpacity,
                  });
                }

                if (boxColor) {
                  const { height } = firstPage.getSize();
                  for (let i = 0; i < s.boundingPolygons.length; i++) {
                    const start = s.boundingPolygons[i];
                    const end =
                      s.boundingPolygons[(i + 1) % s.boundingPolygons.length];
                    firstPage.drawLine({
                      start: { x: start.x * 72, y: height - start.y * 72 },
                      end: { x: end.x * 72, y: height - end.y * 72 },
                      color: boxColor,
                      thickness: 2,
                    });
                  }
                }
              }
            });

            const pdfBytes = await pdfDoc.save();

            if (!pdfBytes || pdfBytes.byteLength === 0) {
              console.error('Byte length is zero size');
              return;
            }

            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            if (!blob || blob.size === 0) {
              console.error('Blob is zero size');
              return;
            }

            const blobUrl = URL.createObjectURL(blob);

            setPdfData(blobUrl);
          }
        }
      }
    };
    loadAndUpdateFile();
  }, [pdfUrl, suggestions, highlightItemId]);

  useEffect(() => {
    setCurrentPageNumber(pageNumber);
  }, [pageNumber]);

  return (
    <>
      {pdfData && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            backgroundColor: 'black',
            padding: '20px',
            height: `calc(100vh - ${theme.spacing(28)} )`,
            width: `calc(100vw - ${menuWidth}px - 500px)`,
            overflow: 'auto',
            boxSizing: 'border-box',
            margin: '0 auto',
          })}
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}>
          <StyledDocument file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
            <Page
              pageNumber={currentPageNumber}
              rotate={currentRotation}
              renderMode="canvas"
              renderTextLayer={false}
              scale={zoomLevel}
            />
          </StyledDocument>
        </Box>
      )}
    </>
  );
};

export default PdfHighlighter;
